import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

const IGNORED = '[data-controller~="photo-gallery"], [data-controller~="lightbox"], video, audio, button, a, input, select, textarea, label, summary, iframe, dialog, [contenteditable]'
const TOUCH_ACTION = ['touch-pan-y', 'touch-pinch-zoom']
const EDGE = 20
const SLOP = 10
const DISTANCE = 0.25
const VELOCITY = 0.5
const VELOCITY_WINDOW = 100
const TILT = 6

export default class extends Controller {
  static values = { previousUrl: String, nextUrl: String, visitAction: { type: String, default: 'advance' } }

  connect () {
    this.onDown = (event) => this.#down(event)
    this.onMove = (event) => this.#move(event)
    this.onUp = (event) => this.#up(event)
    this.onCancel = () => this.#cancel()
    this.onLostCapture = (event) => {
      if (event.target === this.element) this.#cancel()
    }
    this.onZoom = () => this.#zoomed()
    this.onVisit = (event) => {
      if (this.pendingUrl && event.detail.url !== this.pendingUrl) this.#comeBack()
    }
    this.onFetchFailed = (event) => {
      if (this.pendingUrl && String(event.detail.request?.url) === this.pendingUrl) this.#comeBack()
    }

    this.element.addEventListener('pointerdown', this.onDown)
    this.element.addEventListener('pointermove', this.onMove)
    this.element.addEventListener('pointerup', this.onUp)
    this.element.addEventListener('pointercancel', this.onCancel)
    this.element.addEventListener('lostpointercapture', this.onLostCapture)
    window.visualViewport?.addEventListener('resize', this.onZoom)
    document.addEventListener('turbo:visit', this.onVisit)
    document.addEventListener('turbo:fetch-request-error', this.onFetchFailed)
    this.#reset()
    this.#zoomed()
  }

  disconnect () {
    this.element.removeEventListener('pointerdown', this.onDown)
    this.element.removeEventListener('pointermove', this.onMove)
    this.element.removeEventListener('pointerup', this.onUp)
    this.element.removeEventListener('pointercancel', this.onCancel)
    this.element.removeEventListener('lostpointercapture', this.onLostCapture)
    window.visualViewport?.removeEventListener('resize', this.onZoom)
    document.removeEventListener('turbo:visit', this.onVisit)
    document.removeEventListener('turbo:fetch-request-error', this.onFetchFailed)
    window.cancelAnimationFrame(this.frame)
    this.gesture = null
    this.pendingUrl = null
    this.#reset()
  }

  #down (event) {
    if (event.pointerType === 'mouse' || !event.isPrimary || this.gesture || this.pendingUrl) return
    if (this.#zoom > 1 || event.target.closest(IGNORED)) return
    if (event.clientX < EDGE || event.clientX > window.innerWidth - EDGE) return

    this.#reset()
    const top = this.element.getBoundingClientRect().top
    this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, axis: null, samples: [] }
    this.width = this.element.offsetWidth
    this.origin = `50% ${window.innerHeight - top}px`
  }

  #move (event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.id) return

    const dx = event.clientX - gesture.x
    const dy = event.clientY - gesture.y
    if (!gesture.axis) {
      if (Math.hypot(dx, dy) < SLOP) return
      gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      if (gesture.axis === 'y') return this.#forget()
      this.element.setPointerCapture(gesture.id)
      this.element.style.transition = 'none'
      this.element.style.transformOrigin = this.origin
    }

    gesture.dx = this.#neighbour(dx) ? dx : dx * 0.3
    this.#sample(event)
    this.#render()
  }

  #up (event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.id) return
    if (gesture.axis !== 'x') return this.#forget()

    this.#sample(event)
    const url = this.#neighbour(gesture.dx)
    if (url && (Math.abs(gesture.dx) > this.width * DISTANCE || Math.abs(this.#velocity(event.timeStamp)) > VELOCITY)) {
      this.#go(url, gesture.dx)
    } else {
      this.#cancel()
    }
  }

  #neighbour (dx) {
    return dx < 0 ? this.nextUrlValue : this.previousUrlValue
  }

  #sample (event) {
    const samples = this.gesture.samples
    samples.push({ x: event.clientX, t: event.timeStamp })
    if (samples.length > 10) samples.shift()
  }

  #velocity (now) {
    const samples = this.gesture.samples.filter((sample) => now - sample.t <= VELOCITY_WINDOW)
    if (samples.length < 2) return 0
    const first = samples[0]
    const last = samples[samples.length - 1]
    const velocity = (last.x - first.x) / Math.max(last.t - first.t, 1)
    return Math.sign(velocity) === Math.sign(this.gesture.dx) ? velocity : 0
  }

  #go (url, dx) {
    this.#forget()
    this.pendingUrl = new URL(url, document.baseURI).href
    const rotate = this.#rotation(dx)
    this.element.style.transform = `translateX(${dx}px) rotate(${rotate}deg)`
    this.dispatch('navigate', { detail: { direction: dx < 0 ? 'forward' : 'back', url, x: dx, rotate } })
    Turbo.visit(url, { action: this.visitActionValue })
  }

  #render () {
    window.cancelAnimationFrame(this.frame)
    this.frame = window.requestAnimationFrame(() => {
      const dx = this.gesture?.dx ?? 0
      this.element.style.transform = `translateX(${dx}px) rotate(${this.#rotation(dx)}deg)`
    })
  }

  #rotation (dx) {
    if (this.#reducedMotion) return 0
    return Math.max(-TILT, Math.min(TILT, (dx / this.width) * TILT))
  }

  #cancel () {
    const moved = this.gesture?.axis === 'x'
    this.#forget()
    if (moved) this.#springBack()
  }

  #comeBack () {
    this.pendingUrl = null
    this.#springBack()
  }

  #springBack () {
    const style = this.element.style
    if (this.#reducedMotion || !style.transform) return this.#reset()

    this.settled = (event) => {
      if (event.target === this.element) this.#reset()
    }
    this.element.addEventListener('transitionend', this.settled)
    style.transition = 'transform var(--duration-base) var(--ease-out-soft)'
    style.transform = ''
  }

  #forget () {
    window.cancelAnimationFrame(this.frame)
    const gesture = this.gesture
    this.gesture = null
    if (gesture && this.element.hasPointerCapture(gesture.id)) this.element.releasePointerCapture(gesture.id)
  }

  #reset () {
    if (this.settled) this.element.removeEventListener('transitionend', this.settled)
    this.settled = null
    for (const property of ['transform', 'transition', 'transform-origin']) this.element.style.removeProperty(property)
    if (!this.element.style.length) this.element.removeAttribute('style')
  }

  #zoomed () {
    const zoomed = this.#zoom > 1
    TOUCH_ACTION.forEach((name) => this.element.classList.toggle(name, !zoomed))
  }

  get #zoom () {
    return window.visualViewport?.scale ?? 1
  }

  get #reducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}
